'use client';
import { useEffect, useRef, useState } from 'react';
import CloseIcon from '@repo/ui/src/components/Icons/CloseIcon';
import type { FilterConfig } from '@repo/ui/src/primitives/FilterChipDropdown';
import FilterChipDropdown from '@repo/ui/src/primitives/FilterChipDropdown';
import ToggleFilterButton from '@repo/ui/src/primitives/ToggleFilterButton';
import { classNames } from '@repo/ui/src/utils/classNames';
import TabChip from './_TabChip';

type AnimationPhase = 'idle' | 'fade-out' | 'fade-in';

export interface SearchChipTab {
  tabName: string;
  searchType: string;
  redirectUrl?: string;
}

export interface SearchChipsProps {
  tabs: Array<SearchChipTab>;
  activeType: string;
  searchTerm: string;
  tabLabel: string;
  counts?: Record<string, number>;
  onTypeChange: (searchType: string) => void;
  filterConfigs?: Array<FilterConfig>;
  activeFilters?: Record<string, Array<string>>;
  onFilterToggle?: (key: string, value: string) => void;
  onClearAll?: () => void;
  clearAllLabel?: string;
  filtersLoading?: boolean;
}

const SearchChips = ({
  tabs,
  activeType,
  searchTerm,
  tabLabel,
  counts = {},
  onTypeChange,
  filterConfigs = [],
  activeFilters = {},
  onFilterToggle,
  onClearAll,
  clearAllLabel = 'Clear all filters',
  filtersLoading = false,
}: SearchChipsProps) => {
  const hasActiveCategory = activeType !== '' && activeType !== 'All';

  const [phase, setPhase] = useState<AnimationPhase>('idle');
  const [displayedType, setDisplayedType] = useState(activeType);
  const prevHasCategory = useRef(hasActiveCategory);

  useEffect(() => {
    const hadCategory = prevHasCategory.current;
    const nowHasCategory = activeType !== '' && activeType !== 'All';

    if (hadCategory !== nowHasCategory) {
      setPhase('fade-out');
      const t1 = setTimeout(() => {
        setDisplayedType(activeType);
        setPhase('fade-in');
      }, 150);
      const t2 = setTimeout(() => setPhase('idle'), 350);
      prevHasCategory.current = nowHasCategory;
      return () => {
        clearTimeout(t1);
        clearTimeout(t2);
      };
    }

    setDisplayedType(activeType);
    prevHasCategory.current = nowHasCategory;
  }, [activeType]);

  const displayedHasCategory = displayedType !== '' && displayedType !== 'All';

  const visibleTabs = tabs.filter((tab) => {
    const isActive =
      tab.searchType === displayedType || (displayedType === '' && tab.searchType === 'All');
    if (displayedHasCategory && !isActive) return false;
    if (counts[tab.searchType] === 0 && !isActive) return false;
    return true;
  });

  return (
    <nav
      aria-label={tabLabel}
      className={classNames('flex gap-2', !displayedHasCategory && 'overflow-x-auto')}
    >
      <div
        className={classNames(
          'my-4 transition-all duration-200',
          displayedHasCategory
            ? 'flex flex-col items-start gap-2 sm:flex-row sm:flex-wrap sm:gap-x-2 sm:gap-y-4'
            : 'flex flex-nowrap gap-2',
          phase === 'fade-out' && 'opacity-0 translate-x-2',
          phase === 'fade-in' && 'opacity-0 -translate-x-2',
        )}
      >
        {displayedHasCategory ? (
          <>
            {/* X + active tab: one row on mobile, separate items on desktop */}
            <div className="flex gap-2 items-center sm:contents">
              {onClearAll && (
                <button
                  type="button"
                  onClick={onClearAll}
                  aria-label={clearAllLabel}
                  className="flex items-center justify-center flex-none px-3 py-2 transition-colors bg-white border rounded-full cursor-pointer border-warm-300 hover:bg-warm-100"
                >
                  <CloseIcon width="14" color="currentColor" decorative />
                </button>
              )}
              {visibleTabs.map((tab) => {
                const isActive =
                  tab.searchType === displayedType ||
                  (displayedType === '' && tab.searchType === 'All');
                return (
                  <TabChip
                    key={tab.searchType}
                    tab={tab}
                    isActive={isActive}
                    count={counts[tab.searchType]}
                    searchTerm={searchTerm}
                    onTypeChange={onTypeChange}
                  />
                );
              })}
            </div>

            {/* Filter chip shimmer placeholders while loading */}
            {filtersLoading && filterConfigs.length === 0 && (
              <>
                {[1, 2, 3].map((i) => (
                  <span
                    key={i}
                    className="inline-block w-24 rounded-full h-9 bg-warm-200 animate-pulse"
                    aria-hidden="true"
                  />
                ))}
              </>
            )}

            {/* Filter chips — each on its own row on mobile, inline on desktop */}
            {onFilterToggle &&
              filterConfigs.map((config) => {
                const activeValues = activeFilters[config.key] ?? [];
                const isActive = activeValues.length > 0;

                if (config.toggle) {
                  return (
                    <ToggleFilterButton
                      key={config.key}
                      label={config.label}
                      isActive={isActive}
                      onToggle={() => onFilterToggle(config.key, 'true')}
                    />
                  );
                }

                return (
                  <FilterChipDropdown
                    key={config.key}
                    config={config}
                    activeValues={activeValues}
                    onFilterToggle={onFilterToggle}
                  />
                );
              })}
          </>
        ) : (
          visibleTabs.map((tab) => {
            const isActive =
              tab.searchType === displayedType ||
              (displayedType === '' && tab.searchType === 'All');
            return (
              <TabChip
                key={tab.searchType}
                tab={tab}
                isActive={isActive}
                count={counts[tab.searchType]}
                searchTerm={searchTerm}
                onTypeChange={onTypeChange}
              />
            );
          })
        )}
      </div>
    </nav>
  );
};

export default SearchChips;
